import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { restaurantInfo } from "../data/mock";
import { Printer } from "lucide-react";

/**
 * Print-ready business card sheet. Renders 10 Australian-standard business
 * cards (90mm × 55mm) on an A4 page. Print at 100% scale on 300gsm cardstock
 * and slice along the crop marks.
 *
 * Screen preview uses dark-theme styling to match the rest of the site;
 * @media print swaps to pure white/black for accurate colour.
 */

const CARD_QR_URL = "https://mamaminassoulfeed.online";

function CardFront() {
  return (
    <div className="card-face card-front">
      <div className="card-front-corner card-front-corner-tl" />
      <div className="card-front-corner card-front-corner-br" />
      <div className="card-front-body">
        <img
          src="/media/logo-300.png"
          alt="Mamamina's Soulfeed"
          className="card-front-logo"
        />
      </div>
      <div className="card-front-footer">
        <span className="card-halal">حلال · HALAL</span>
        <span className="card-tag">Brisbane</span>
      </div>
    </div>
  );
}

function CardBack() {
  return (
    <div className="card-face card-back">
      <div className="card-back-left">
        <div className="card-back-name">Mamamina&apos;s Soulfeed</div>
        <div className="card-back-line">{restaurantInfo.contact.mobileDisplay}</div>
        <div className="card-back-line-muted">or {restaurantInfo.contact.phoneDisplay}</div>
        <div className="card-back-line">{restaurantInfo.contact.email}</div>
        <div className="card-back-line-muted">{restaurantInfo.address.line1}</div>
        <div className="card-back-line-muted">{restaurantInfo.address.line2}</div>
        <div className="card-back-social">@mamaminassoulfeed_</div>
      </div>
      <div className="card-back-right">
        <QRCodeSVG value={CARD_QR_URL} size={70} bgColor="#ffffff" fgColor="#1a1512" level="M" />
        <div className="card-back-url">mamaminassoulfeed.online</div>
      </div>
    </div>
  );
}

export default function PrintBusinessCard() {
  return (
    <div className="print-shell">
      <PrintStyles />

      <header className="print-header no-print">
        <div>
          <h1>Business card · Print sheet</h1>
          <p>10 cards per A4 sheet · 90 × 55 mm · Recommended 300 gsm matte or soft-touch cardstock</p>
        </div>
        <button className="print-btn" onClick={() => window.print()} data-testid="print-card-btn">
          <Printer size={16} /> Print / Save PDF
        </button>
      </header>

      {/* FRONT PAGE — 10 fronts */}
      <div className="a4-page">
        <div className="page-label no-print">FRONTS · Page 1</div>
        <div className="card-grid">
          {Array.from({ length: 10 }).map((_, i) => (
            <CardFront key={`front-${i}`} />
          ))}
        </div>
      </div>

      {/* BACK PAGE — 10 backs, mirrored order for duplex printing */}
      <div className="a4-page">
        <div className="page-label no-print">BACKS · Page 2 (flip on short edge)</div>
        <div className="card-grid card-grid-mirrored">
          {Array.from({ length: 10 }).map((_, i) => (
            <CardBack key={`back-${i}`} />
          ))}
        </div>
      </div>

      <footer className="no-print print-footer">
        <p>Two-page duplex print. On your printer dialog set: <strong>A4</strong>, <strong>100% scale</strong>, <strong>Two-sided: Flip on short edge</strong>. Trim along card edges.</p>
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Print styles — screen preview + print output                       */
/* ------------------------------------------------------------------ */
function PrintStyles() {
  return (
    <style>{`
      /* Screen preview — dark theme, centered */
      .print-shell {
        min-height: 100vh;
        background: #0f0b09;
        color: #e4dcc9;
        padding: 32px 16px 64px;
        font-family: -apple-system, "Segoe UI", sans-serif;
      }
      .print-header {
        max-width: 800px; margin: 0 auto 24px; display: flex;
        align-items: center; justify-content: space-between; gap: 16px;
        flex-wrap: wrap;
      }
      .print-header h1 { font-size: 22px; margin: 0 0 4px; color: #f6ebd0; }
      .print-header p { font-size: 12px; color: #a09484; margin: 0; }
      .print-btn {
        display: inline-flex; align-items: center; gap: 8px;
        background: #B7853D; color: #1a1512; border: none;
        padding: 10px 18px; font-weight: 600; cursor: pointer;
        border-radius: 4px; font-size: 13px;
      }
      .print-btn:hover { filter: brightness(1.1); }
      .print-footer { max-width: 800px; margin: 24px auto 0; color: #a09484; font-size: 12px; }
      .page-label { text-align: center; font-size: 10px; letter-spacing: 3px;
        text-transform: uppercase; color: #7a6f5f; margin-bottom: 10px; }

      /* A4 page container */
      .a4-page {
        width: 210mm;
        min-height: 297mm;
        margin: 0 auto 24px;
        background: #ffffff;
        color: #1a1512;
        padding: 15mm 10mm;
        box-shadow: 0 4px 24px rgba(0,0,0,.4);
        page-break-after: always;
      }
      /* 5 rows × 2 cols = 10 cards, exact Australian card size */
      .card-grid {
        display: grid;
        grid-template-columns: 90mm 90mm;
        grid-template-rows: repeat(5, 55mm);
        gap: 0;
        justify-content: center;
      }
      .card-grid-mirrored {
        direction: rtl;
      }
      .card-grid-mirrored .card-face { direction: ltr; }

      /* Individual card face */
      .card-face {
        width: 90mm; height: 55mm;
        position: relative;
        overflow: hidden;
        break-inside: avoid;
        border: 0.2mm dashed #d4c9b0;   /* light cut guide, becomes invisible when trimmed */
      }
      @media print {
        .card-face { border: 0.15mm dashed #cccccc; }
      }

      /* --- Front face --- */
      .card-front {
        background: #1a1512;
        color: #f6ebd0;
        display: flex;
        flex-direction: column;
        padding: 6mm 7mm;
      }
      .card-front-corner {
        position: absolute; width: 12mm; height: 12mm;
        border: 1px solid #B7853D;
      }
      .card-front-corner-tl { top: 3mm; left: 3mm; border-right: none; border-bottom: none; }
      .card-front-corner-br { bottom: 3mm; right: 3mm; border-left: none; border-top: none; }
      .card-front-body { flex: 1; display: flex; flex-direction: column;
        justify-content: center; align-items: center; text-align: center; }
      .card-front-logo {
        max-width: 68mm;
        max-height: 42mm;
        object-fit: contain;
        display: block;
        margin: 0 auto;
        filter: drop-shadow(0 0 4mm rgba(183, 133, 61, 0.15));
      }
      .card-front-mono {
        width: 10mm; height: 10mm; border-radius: 50%;
        border: 0.4mm solid #B7853D; color: #B7853D;
        display: flex; align-items: center; justify-content: center;
        font-family: Georgia, serif; font-size: 6mm; font-style: italic;
        margin-bottom: 2.5mm;
      }
      .card-front-name {
        font-family: Georgia, serif; font-size: 5mm;
        color: #f6ebd0; letter-spacing: 0.1mm;
      }
      .card-front-name-italic {
        font-family: Georgia, serif; font-style: italic;
        font-size: 6.2mm; color: #B7853D; margin-top: -0.8mm;
      }
      .card-front-tagline {
        font-size: 2.2mm; letter-spacing: 1mm; color: #a09484;
        margin-top: 2.5mm; text-transform: uppercase;
      }
      .card-front-footer {
        display: flex; justify-content: space-between; align-items: center;
        font-size: 2mm; letter-spacing: 0.7mm; color: #7a6f5f;
        text-transform: uppercase; padding-top: 2mm;
      }
      .card-halal { color: #B7853D; }

      /* --- Back face --- */
      .card-back {
        background: #f6ebd0;
        color: #1a1512;
        display: flex;
        padding: 6mm 7mm;
        gap: 4mm;
        align-items: center;
      }
      .card-back-left { flex: 1; min-width: 0; }
      .card-back-name {
        font-family: Georgia, serif; font-style: italic;
        font-size: 4mm; color: #1a1512; margin-bottom: 2mm;
        border-bottom: 0.3mm solid #B7853D;
        padding-bottom: 1.5mm; width: fit-content;
      }
      .card-back-line { font-size: 2.6mm; color: #1a1512; margin-top: 0.6mm; font-weight: 600; }
      .card-back-line-muted { font-size: 2.4mm; color: #5a4d40; margin-top: 0.4mm; }
      .card-back-social {
        font-size: 2mm; letter-spacing: 0.5mm; color: #B7853D;
        text-transform: lowercase; margin-top: 2.5mm; font-weight: 600;
      }
      .card-back-right {
        display: flex; flex-direction: column; align-items: center; gap: 1.5mm;
        flex-shrink: 0;
      }
      .card-back-url {
        font-size: 2mm; color: #5a4d40; letter-spacing: 0.3mm;
      }

      /* Print output */
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        body, html { background: white !important; margin: 0; }
        .print-shell { background: white; padding: 0; }
        .no-print { display: none !important; }
        .a4-page { box-shadow: none; margin: 0; }
        /* Force colours in print */
        .card-front, .card-back {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `}</style>
  );
}
